import { fabric } from 'fabric';
import Client from '../collaboration/Client';
import overrides from '../fabric/overrides';
import DeviceTypeDetector from '../utils/DeviceTypeDetector';
import { setZoom } from '../utils/zoom';
import BackgroundBehaviors from './BackgroundBehaviors';
import CanvasLayerBehaviors from './CanvasLayerBehaviors';
import ExternalContent from './ExternalContent';
import Serialization, { type DocData } from '../collaboration/Serialization';
import {
  zManagerSortCollection,
  zManagerMove,
  getMaxZ,
  Z_INCREMENT,
  type MoveOptions,
} from '../utils/zManager';
import {
  CONTEXT_MENU_OBJECT,
  CONTEXT_MENU_CANVAS,
  CONTEXT_MENU_TABLE,
} from '../tools/ToolMenu';
import paper from 'paper';
import type WhiteboardController from '../controllers/WhiteboardController';
import type { IPoint } from 'fabric/fabric-impl';
import {
  hasFrameBehaviors,
  hasFrameMemberBehaviors,
  hasLayout,
  hasReadOnlyBehaviors,
  isComment,
  isFrame,
  isSimpleGroup,
  isSimpleSelection,
  isTable,
} from './guards';
import { copyWhiteboardContents } from '../tools/ToolCopy';
import { type Rect, type RecordId, AttachmentPoint } from '../types';
import type ReadOnlyBehaviors from './ReadOnlyBehaviors';
import CanvasPersistentControlBehaviors from './CanvasPersistentControlBehaviors';
import Debug from 'debug';
import { isRightClick } from '../utils/click';
import { _isMac } from '../utils/Shortcut';
import type ControlBase from '../fabric/ControlBase';
import { generateJitteredKeyBetween } from 'fractional-indexing-jittered';
import SimpleSelection from 'fabric/SimpleSelection';
import TouchEventManager from '../utils/TouchEventManager';
import type FrameBehaviors from './FrameBehaviors';
import ExportBehaviors, { ExportMode } from './ExportBehaviors';

const debug = Debug('whiteboard:rendering');

overrides();

const RIGHT_CLICK_PAN_MINIMUM_DRAG = 5;

interface Canvas extends fabric.Canvas, BackgroundBehaviors, ExportBehaviors {}

@CanvasPersistentControlBehaviors
@BackgroundBehaviors
@CanvasLayerBehaviors
@ExportBehaviors
class Canvas extends fabric.Canvas {
  isDrawingMode = false;
  uniformScaling = false;
  // Needs to be true so that you can easily select an object the is surrounded by another selected object.
  preserveObjectStacking = true;
  fireRightClick = true;
  stopContextMenu = true;
  selectionBorderColor = '#5ca5e0';
  selectionColor = 'rgba(0, 157,207, 0.3)';
  selectionLineWidth = 1;
  brush?: boolean;

  snappedMovementKey = 'shiftKey';
  // The Mac ⌘ key is the meta key. We enable both ctrl for non-mac platforms
  // as well as meta for mac platforms.
  selectionKey = ['metaKey', 'shiftKey', ...(_isMac ? [] : ['ctrlKey'])];

  readOnly = false;
  allowUpdate = false;
  allowAnnotate = false;

  _rightClickPan = false;
  actionObjects = [];
  _dragStart: IPoint | null = null;
  exporting = ExportMode.NONE;

  renderedObjects: Array<fabric.Object> = [];

  controller: WhiteboardController;
  containerElement: HTMLElement;
  externalElement: HTMLDivElement;
  externalContent: ExternalContent;
  canvasResizeObserver: ResizeObserver;
  wheelEventHandler: (event: WheelEvent) => void;
  _temporaryHideObjectToolbarTimer?: NodeJS.Timeout;
  _groupSelector: { top: number; left: number; ex: number; ey: number } | null;
  _target: fabric.Object | null;
  private shouldDuplicate = false;

  // Whether the mouse has moved since the last mousedown event.
  mouseHasMoved = false;

  // Device type detector for distinguishing between mouse and trackpad
  private readonly deviceTypeDetector = new DeviceTypeDetector();

  // Touch event manager for handling touch gestures
  private readonly touchEventManager: TouchEventManager;

  // this exists at runtime, but not on the Canvas type
  indexOf: (obj: fabric.Object) => number;
  contextMenuHandler: (event: MouseEvent) => void;

  lastMouseDown?: MouseEvent;

  // Because zManagerSortCollection might be expensive, we want to avoid calling it more than needed.
  // Rather than calling directly, set the collectionNeedsSort flag whenever something relevant changes.
  // Then just before render, the collection will be sorted and the flag unset.
  collectionNeedsSort: boolean = false;

  // List of callbacks fired when the user changes their selection on the canvas
  selectionChangeListeners: Array<(objects: fabric.Object[]) => void> = [];

  constructor(
    controller: WhiteboardController,
    containerElement: HTMLDivElement,
    canvasElement: HTMLCanvasElement,
    externalElement: HTMLDivElement,
    public portalElement: HTMLDivElement,
    options: Record<string, unknown>
  ) {
    super(canvasElement, options);

    // Provide global access to the controller. Makes it accessible inside objects.
    this.controller = controller;
    this.containerElement = containerElement;
    this.externalElement = externalElement;

    this.externalContent = new ExternalContent(this.externalElement, this);

    // @ts-expect-error Paper's types are wrong, it's valid to call this with no
    // arguments - see Project constructor
    paper.setup();

    // Fit the canvas to the container, and keep it fitting.
    this.canvasResizeObserver = new ResizeObserver(() => {
      this.fitCanvasToContainer();
    });
    this.canvasResizeObserver.observe(this.containerElement);
    this.fitCanvasToContainer();

    // Suppress the browser context menu unless editing.
    this.contextMenuHandler = (event: MouseEvent) =>
      this.showBrowserMenu(event);
    this.containerElement.addEventListener(
      'contextmenu',
      this.contextMenuHandler
    );

    this.on('mouse:down', ({ e }) => {
      this.controller.actionHideContextMenu();
      this.controller.actionHideConnectorPointMenu();
      this.controller.actionHideConnectorShapeMenu();

      if (e.altKey) {
        this.shouldDuplicate = true;
      }
      this.lastMouseDown = e;
      this.mouseHasMoved = false;
    });

    this.on('mouse:up', eventData => {
      this._dragStart = null;
      this.shouldDuplicate = false;

      // We don't want to show the context menu if the user was just panning.
      if (this._rightClickPan) {
        this._endRightClickPan(eventData);
      } else {
        this._mouseClick(eventData);
      }
    });

    // Use _panAndZoomWheel for window pinch-to-zoom events to prevent window
    // zoom on objects not inside canvas-container
    this.wheelEventHandler = event => this.handleWindowWheel(event);
    window.addEventListener('wheel', this.wheelEventHandler, {
      passive: false,
    });

    window.addEventListener('keydown', this.handleKeyDown);

    // Track all mouse movements so we can get current location without a
    // mouse event (e.g. in response to keyboard events).
    this.on('mouse:move', eventData => {
      this.mouseHasMoved = true;

      if (!eventData.absolutePointer) {
        return;
      }

      if (this.shouldDuplicate) {
        this.shouldDuplicate = false;

        // only duplicate if the user is still holding the alt key when the mouse starts moving
        if (eventData.e.altKey) {
          const selectedObjects = this.duplicateActiveSelection();
          if (selectedObjects)
            if (this._currentTransform) {
              // alter the current transform to be the duplicated objects instead of the original,
              // which were selected in the initial mousedown
              this._finalizeCurrentTransform(eventData.e);
              this._currentTransform = null;
            }
          this._setupCurrentTransform(eventData.e, selectedObjects, true);
        }
      }

      this.controller.mousePosition = eventData.absolutePointer;

      const rightButtonHeld = eventData.e?.buttons === 2;

      // Panning should only be enabled once the user has dragged a small
      // distance with the right button held down
      let shouldStartRightClickPan = false;
      if (this._dragStart && !this._rightClickPan) {
        const { x: initialX, y: initialY } = this._dragStart;
        const { x, y } = this.controller.mousePosition;

        const deltaX = Math.abs(x - initialX);
        const deltaY = Math.abs(y - initialY);

        shouldStartRightClickPan =
          Math.sqrt(deltaX ** 2 + deltaY ** 2) > RIGHT_CLICK_PAN_MINIMUM_DRAG;
      }

      if (!this._dragStart && rightButtonHeld && !this._rightClickPan) {
        this._dragStart = this.controller.mousePosition;
      }

      if (!this._rightClickPan && shouldStartRightClickPan) {
        this._startRightClickPan(eventData);
      }

      if (this._rightClickPan) {
        this.freeDrawingBrush.onMouseMove(eventData.absolutePointer, eventData);
      }
    });

    this.animateEdgePan = this.animateEdgePan.bind(this);

    // Handle behaviors for locked objects.
    this.on({
      'selection:created': e => {
        this.updateSelectionRotationControlVisibility(e);
        this.handleLockedObjectsInSelection(e);
      },
      'selection:updated': e => {
        this.updateSelectionRotationControlVisibility(e);
        this.handleLockedObjectsInSelection(e);
      },
    });

    // Hide and show the toolbar during operations that might move objects.
    this.on({
      'selection:created': () => this.showObjectToolbar(),
      'selection:updated': () => this.showObjectToolbar(),
      'selection:cleared': () => this.hideObjectToolbar(),

      'object:moving': () => this.beginningOperation(),
      'object:scaling': () => this.beginningOperation(),
      'object:rotating': () => this.beginningOperation(),
      'object:skewing': () => this.beginningOperation(),

      'object:moved': () => this.completedOperation(),
      'object:scaled': () => this.completedOperation(),
      'object:rotated': () => this.completedOperation(),
      'object:skewed': () => this.completedOperation(),
    });

    // Expose user selections to external interests
    this.on({
      'selection:created': () => this.fireSelectionChangeCallbacks(),
      'selection:updated': () => this.fireSelectionChangeCallbacks(),
      'selection:cleared': () => this.fireSelectionChangeCallbacks(),
    });

    this.on({
      'object:moving': eventData => this.calculateEdgePanVector(eventData),
      'object:scaling': eventData => this.calculateEdgePanVector(eventData),
    });

    this.on('mouse:wheel', eventData => this._panAndZoomWheel(eventData));

    // Initialize touch event manager for pinch zoom and two-finger pan
    this.touchEventManager = new TouchEventManager(
      this.controller,
      this.containerElement,
      this
    );
    this.touchEventManager.initialize();

    // Add path to collaboration client. We get this *before* it is added
    // to the canvas.
    this.on('before:path:created', ({ path }) => path.created(this.controller));
    this.on('path:created', ({ path }) => path.fire('created'));

    this.on('object:added', ({ target }) => {
      if (target.authorId) {
        void this.controller.actionResolveUserInfo(target.authorId).then(() => {
          // Nothing, just resolve
        });
      }

      if (target.reactions) {
        const allReactions = Object.values(target.reactions).reduce(
          (acc, votes) => ({ ...acc, ...votes }),
          {}
        );

        const userIds = Object.keys(allReactions);

        void Promise.all(
          userIds.map(
            async userId =>
              await this.controller.actionResolveUserInfo(userId as RecordId)
          )
        ).then(() => {
          // Nothing, just resolve
        });
      }
    });

    this.on('object:added', ({ target }) => {
      if (Client.ignoredObjectTypes.includes(target.type)) return;

      if (hasReadOnlyBehaviors(target)) {
        this.setObjectReadOnly(target);
      }
    });

    // Reset the `preserveObjectStacking` setting. It may have been updated
    // when double clicking to drill into a group.
    this.on('selection:updated', () => (this.preserveObjectStacking = true));

    // Brushes don't redraw during pan and zoom so we force a redraw.
    const redrawBrushes = () => {
      if (
        this.isDrawingMode &&
        this.freeDrawingBrush?._render &&
        (this.freeDrawingBrush._points?.length ?? 0) >= 2
      ) {
        this.clearContext(this.contextTop);
        this.freeDrawingBrush._render();
      }
    };
    this.on('pan', redrawBrushes);
    this.on('zoom', redrawBrushes);

    // Handle collaborative cursor events as if they were canvas events
    this.on('cursor:mousemove', this._onMouseMove);
    this.on('cursor:mousedown', this._onMouseDown);
    this.on('cursor:mouseup', this._onMouseUp);

    this.on('mouse:dblclick', event => {
      const target = event.target;
      if (!target) return;

      const corner = event.target?.__corner;
      if (!corner) return;

      const control = target.controls[corner] as ControlBase;
      if (!control?.dblClickHandler) return;

      control.dblClickHandler(event, target);
    });

    this.setReadOnly(
      controller.isReadOnly(),
      controller.props.allowUpdate,
      controller.props.allowAnnotate
    );

    // Elements that use a custom font are initially rendered with a
    // fallback font and updated on rerender. Force the rerender once
    // the fonts have finished loading.
    void document.fonts.ready.then(() => this.requestRenderAll());

    this.setupErrorHandling();
    this.setupObjectChanged();
  }

  // Fires a keydown event on the active object.
  // Added to handle key presses for table cells.
  handleKeyDown = (e: KeyboardEvent) => {
    const activeObject = this.getActiveObject();
    activeObject?.fire('keydown', { e });
  };

  _createActiveSelection(target, e) {
    if (this.handleFrameSelectionLogic(target)) {
      return;
    }
    super._createActiveSelection(target, e);
  }

  _updateActiveSelection(target, e) {
    if (this.handleFrameSelectionLogic(target)) {
      return;
    }

    // Remove frame members from selection when selecting their parent frame
    if (hasFrameBehaviors(target)) {
      this.removeFrameMembersFromSelection(target);
    }

    // Handle selection based on current state
    const activeObject = this.getActiveObject();
    if (activeObject && isSimpleSelection(activeObject)) {
      return super._updateActiveSelection(target, e);
    } else if (activeObject) {
      this._createActiveSelection(target, e);
    } else {
      this.setActiveObject(target);
    }
  }

  // Extracted common frame selection logic
  private handleFrameSelectionLogic(target) {
    // Target is a frame member
    if (hasFrameMemberBehaviors(target)) {
      const frame = target.inFrame();
      if (frame) {
        const activeObject = this.getActiveObject();

        // If frame is selected, replace it with the member
        if (activeObject?.id === frame.id) {
          this.replaceSelectionWith(target);
          return true;
        }

        // Remove parent frame from selection if present
        if (activeObject && isSimpleSelection(activeObject)) {
          const hadFrame = this.removeObjectFromSelection(
            activeObject,
            obj => obj.id === target.container
          );

          // If selection is now empty after removing frame, select the target
          if (hadFrame && activeObject.getObjects().length === 0) {
            this.replaceSelectionWith(target);
            return true;
          }
        }
      }
    }

    // Target is a frame with a member already selected
    if (hasFrameBehaviors(target)) {
      const activeObject = this.getActiveObject();
      if (
        hasFrameMemberBehaviors(activeObject) &&
        activeObject.container === target.id
      ) {
        this.replaceSelectionWith(target);
        return true;
      }
    }

    return false;
  }

  // Helper function to replace current selection with a new object
  replaceSelectionWith(object: fabric.Object) {
    this.discardActiveObject();
    this.setActiveObject(object);
    this.requestRenderAll();
  }

  // Helper function to remove members that match predicate from a selection
  private removeObjectFromSelection(selection, predicate) {
    let found = false;
    selection.getObjects().forEach(obj => {
      if (predicate(obj)) {
        selection.removeWithUpdate(obj);
        found = true;
      }
    });
    return found;
  }

  // Helper function to remove frame members from selection
  private removeFrameMembersFromSelection(frame) {
    const activeObject = this.getActiveObject() as SimpleSelection;
    if (!activeObject) return;

    this.removeObjectFromSelection(
      activeObject,
      obj => hasFrameMemberBehaviors(obj) && obj.container === frame.id
    );
  }

  _collectObjects(data: { e: MouseEvent }): fabric.Object[] {
    // Tables have sophisticated logic which determines if they group selector
    // is selecting cells of the table. In that case, we should only be
    // selecting cells and not objects. So if we detect a table that confirms
    // the selection is for cells of that table, return nothing.
    if (this.getObjects().some(obj => isTable(obj) && obj.isSelectingCells())) {
      return [];
    }

    // @ts-expect-error augmenting a private fabric method here
    let objects: fabric.Object[] = super._collectObjects(data);

    objects = SimpleSelection.getFilteredObjectsForSelection(objects);

    return objects;
  }

  // Cleanup the canvas.
  deleted() {
    this.externalContent.destroy();

    this.canvasResizeObserver.disconnect();
    window.removeEventListener('wheel', this.wheelEventHandler);
    window.removeEventListener('keydown', this.handleKeyDown);

    this.containerElement.removeEventListener(
      'contextmenu',
      this.contextMenuHandler
    );

    // Clean up touch event manager
    this.touchEventManager.destroy();

    // Clear event listeners
    this.selectionChangeListeners = [];

    this.dispose();
    this.teardownObjectChanged();
  }

  addObjects(objects: fabric.Object[], options?: { where?: MoveOptions }) {
    const collection = this._objects;

    objects.forEach(object => {
      collection.push(object);
      this._onObjectAdded(object);

      if (object.hasExternalContent()) {
        this.externalContent.add(object);
      }
    });

    if (options?.where) {
      zManagerMove(objects, options.where);
    }

    // Ensure that the objects are sorted into their layers.
    this.collectionNeedsSort = true;

    this.renderOnAddRemove && this.requestRenderAll();
    this.controller.actionSetIsEmpty();

    return this;
  }

  // Add an object to the canvas in the order of its z order.
  add(...objects: fabric.Object[]) {
    // if any object has no z-value, allocate it one at the top of the stack
    let z: number | undefined;
    for (const obj of objects.filter(o => o.z === undefined || o.z === null)) {
      if (z === undefined) {
        z = Math.max(getMaxZ(this._objects), getMaxZ(objects));
      }
      obj.set('z', (z += Z_INCREMENT));
    }

    this.addObjects(objects);
    return this;
  }

  // When removing an object, make sure we are not in the process of operating
  // on it already. If so, clean up that state.
  remove(...objects: fabric.Object[]) {
    objects.forEach(object => {
      if (
        this._currentTransform &&
        object.id &&
        this._currentTransform.target?.id === object.id
      ) {
        this._finalizeCurrentTransform({} as Event);
        this._currentTransform = null;

        if (this.freeDrawingBrush.objectInProgress?.() === object) {
          // if we are drawing an object and a collaborator removes it, just clear
          // the invalid brush by activating the pointer
          this.controller.actionSetStylus(this.controller.stylusPointer);
        }
      }

      if (object.hasExternalContent()) {
        this.externalContent.remove(object);
      }
    });

    this.controller.actionSetIsEmpty();

    super.remove(...objects);

    return this;
  }

  completedOperation() {
    this.endEdgePanAnimation();
    this.controller.actionEndObjectTransformation();
    this.showObjectToolbar();

    this.controller.undoManager.completeUndoGroup();
  }

  fitCanvasToContainer(
    moveContent?: { x: number; y: number } | number[] | 'fitContent'
  ) {
    // when this is called just due to the element resizing, we don't move the content at all
    // for cases where we want to move the content, e.g. transitioning to fullScreen, we
    // preemptively call this method with the details

    if (this.canvasElementInitialized) {
      // Canvas container dimensions

      const width = this.containerElement.offsetWidth;
      const height = this.containerElement.offsetHeight;

      const shouldSetDimensions =
        width !== this.getWidth() || height !== this.getHeight();

      // don't do anything if the dims already match - they may have been set already
      if (shouldSetDimensions) {
        this.setDimensions({ width, height });
      }

      if (moveContent) {
        if (moveContent === 'fitContent') {
          this.controller.actionZoomIntoView();
        } else if (Array.isArray(moveContent)) {
          // go direct to supplied viewport
          this.setViewportTransform(moveContent);
        } else {
          // determine translated viewport from x/y values
          const newVpt = [...this.viewportTransform!];
          newVpt[4] += moveContent.x;
          newVpt[5] += moveContent.y;
          this.setViewportTransform(newVpt);
        }
        this.fire('pan');
      }

      if (shouldSetDimensions || moveContent) {
        // setDimensions requests render in the next animation frame, but changing the canvas element dimensions
        // clears its content. So an animation frame is already underway, the canvas ends up blank until the
        // next one. So we force an immediate render, since a slower frame is better than a flickering canvas
        this.renderAll();
        this.controller.actionForceRerenderObjectToolbar();
      }
    }
  }

  vptCoordsAsWindowCoords(): Rect | null {
    const vpt = this.vptCoords;

    if (!vpt) {
      return null;
    }

    return this.rectToWindowCoords({
      left: vpt.tl.x,
      top: vpt.tl.y,
      width: vpt.tr.x - vpt.tl.x,
      height: vpt.bl.y - vpt.tl.y,
    });
  }

  // Convert a rectangle in canvas coordinates to the browser window coordinates
  // so we can position toolbars around it.
  rectToWindowCoords(rect: Rect): Rect {
    const zoom = this.getZoom();
    const transform = this.viewportTransform;

    return {
      left: transform![4] + rect.left * zoom,
      top: transform![5] + rect.top * zoom,
      width: rect.width * zoom,
      height: rect.height * zoom,
    };
  }

  // Convert a point in window coordinates to canvas coordinates.
  pointToCanvasCoords(point): IPoint {
    const zoom = this.getZoom();
    const transform = this.viewportTransform!;

    return {
      x: (point.x - transform[4]) / zoom,
      y: (point.y - transform[5]) / zoom,
    };
  }

  // Convert a point in canvas coordinates to the window coordinates,
  // returns null if the point is outside the window.
  pointToWindowCoords(point): IPoint | null {
    const zoom = this.getZoom();
    const transform = this.viewportTransform!;

    const windowPoint = {
      x: point.x * zoom + transform[4],
      y: point.y * zoom + transform[5],
    };

    // Check that point is visible in the window.
    if (
      windowPoint.x < 0 ||
      windowPoint.y < 0 ||
      windowPoint.x > this.containerElement.offsetWidth ||
      windowPoint.y > this.containerElement.offsetHeight
    ) {
      return null;
    } else {
      return windowPoint;
    }
  }

  // Converts a rect to page coordinates
  rectToPageCoordinates(rect: Rect): Rect {
    const windowCoords = this.rectToWindowCoords(rect);
    const clientRect = this.containerElement.getBoundingClientRect();

    return {
      left: windowCoords.left + clientRect.x,
      top: windowCoords.top + clientRect.y,
      width: windowCoords.width,
      height: windowCoords.height,
    };
  }

  // Debugging helper to draw a point on the canvas so we can visualize
  // coordinates.
  tracePoint(point) {
    this.addObjects(
      [
        new fabric.Circle({
          left: point.x - 3,
          top: point.y - 3,
          radius: 3,
          fill: 'rgba(255,0,0,0.2)',
          opacity: 1.0,
          fillOpacity: 0.5,
        }),
      ],
      { where: 'top' }
    );
  }

  traceScale(ctx) {
    ctx.strokeStyle = 'red';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(100, 0);
    ctx.lineTo(0, 0);
    ctx.lineTo(0, 100);
    // Tick marks
    ctx.moveTo(100, -5);
    ctx.lineTo(100, 5);
    ctx.moveTo(50, 0);
    ctx.lineTo(50, 5);
    ctx.moveTo(-5, 100);
    ctx.lineTo(5, 100);
    ctx.moveTo(0, 50);
    ctx.lineTo(5, 50);
    ctx.stroke();
  }

  traceGrid(ctx) {
    ctx.save();
    ctx.strokeStyle = '#dedede';
    ctx.lineWidth = 1;
    ctx.beginPath();
    for (let i = -1000; i < 1000; i += 10) {
      ctx.moveTo(i, -1000);
      ctx.lineTo(i, 1000);
      ctx.moveTo(-1000, i);
      ctx.lineTo(1000, i);
    }
    ctx.stroke();

    ctx.fillStyle = '#aaa';
    for (let i = -1000; i < 1000; i += 50) {
      if (i !== 0) {
        ctx.fillText(i, i - 10, -5);
        ctx.fillText(i, -5, i + 5);
      }
    }
    ctx.restore();
  }

  traceRouter(ctx) {
    ctx.save();

    /*
    // Show rulers
    ctx.strokeStyle = '#99d';
    ctx.beginPath();
    for (const ruler of this.traceConnector.vRulers) {
      ctx.moveTo(ruler, -10000);
      ctx.lineTo(ruler, 10000);
    }
    for (const ruler of this.traceConnector.hRulers) {
      ctx.moveTo(-10000, ruler);
      ctx.lineTo(10000, ruler);
    }
    ctx.stroke();
    */

    ctx.strokeStyle = '#ddd';
    ctx.lineWidth = 1;
    ctx.beginPath();
    for (const connection of this.traceConnector.connections) {
      ctx.moveTo(connection.a.x, connection.a.y);
      ctx.lineTo(connection.b.x, connection.b.y);
    }
    ctx.stroke();

    ctx.strokeStyle = 'red';
    ctx.font = '4px serif';
    for (const spot of this.traceConnector.spots) {
      ctx.strokeRect(spot.x - 1, spot.y - 1, 3, 3);
      // Show text coordinates of each grid point. Very noisy.
      // ctx.fillText(`${spot.x},${spot.y}`, spot.x - 10, spot.y - Math.random() * 10);
    }

    ctx.restore();
  }

  handleWindowWheel(event: WheelEvent) {
    // Only act on elements that are inside this whiteboard. For whiteboards
    // embedded in a custom field or note, do not block scrolling
    const isEmbedded =
      this.containerElement.closest('.aha-editor-field') !== null;

    const target = event.target as HTMLElement;

    // We want to enable scrolling within tippy.js popups so that it works in
    // the icon selector component and similar elements.
    const isPopup = target.closest('[data-tippy-root]') !== null;

    // Allow template modal to scroll
    const isTemplateModal =
      target.closest('[class^="templateModal"], [class^="previewModal"]') !==
      null;

    const isSidePanel = target.closest('[class*="sidePanel"]') !== null;

    // Prevent scrolling on CSX elements. Safe to check alone as whiteboard
    // must be full screen to edit
    const isCanvasXInput = target.closest('.canvasx-text-input') !== null;

    // @ts-expect-error path is not a standard property of Event. Check against composedPath and
    // fall back to path
    const path = event?.composedPath() ?? event.path;

    if (
      (this.containerElement && // controller may not yet be instantiated
        path?.includes(this.containerElement) &&
        !isEmbedded &&
        !isPopup &&
        !isTemplateModal &&
        !isSidePanel) ||
      isCanvasXInput
    ) {
      event.preventDefault();
      event.stopPropagation();
      return false;
    }
  }

  _renderObjects(ctx: CanvasRenderingContext2D, objects: fabric.Object[]) {
    if (this.collectionNeedsSort) {
      zManagerSortCollection(objects);
      this.collectionNeedsSort = false;
    }

    debug(`Starting render`);
    // Clip out space for any objects with HTML.
    ctx.save(); // Save the starting clip state.
    const externalContentObjects = objects.filter(
      object => object.hasExternalContent() && object.clipExternalContent()
    );
    let externalContentLevel = 0;
    this.clipExternalContentObjects(
      ctx,
      externalContentObjects,
      externalContentLevel
    );

    if (!this.readOnly && this.controller.state.showBackgroundGrid) {
      this.renderBackgroundGrid(ctx);
    }

    const { enableGrid, enableScale, enableOrthogonalRouterDebug } =
      this.controller.state.debug;

    // Add this when debugging positioning:
    if (enableGrid) {
      this.traceGrid(ctx);
    }

    if (enableScale) {
      this.traceScale(ctx);
    }

    if (this.isHTMLExport()) {
      // Clean up any existing foreignObjectRoots if we have rendered before
      this.cleanupForeignObjectRoots();
    }

    this.renderedObjects = [];
    objects.forEach(object => {
      object.wasRendered = false;
      object.render(ctx);
      if (object.wasRendered) {
        this.renderedObjects.push(object);
      }

      if (this.isHTMLExport()) {
        // Handle external content rendering
        // Use foreignObject for SVG/HTML export
        this.renderExternalContentToSVG(ctx, object);

        if (isSimpleGroup(object)) {
          this.recursivelyRenderExternalContent(ctx, object);
        }
      } else {
        // Now only clip objects that are above this one.
        if (object.hasExternalContent() && object.clipExternalContent()) {
          this.clipExternalContentObjects(
            ctx,
            externalContentObjects,
            ++externalContentLevel
          );
        }
      }
    });

    if (enableOrthogonalRouterDebug && this.traceConnector) {
      this.traceRouter(ctx);
    }

    ctx.restore(); // Reset the clip state.

    debug(`Finished render of ${objects.length} objects`);
  }

  clipExternalContentObjects(
    ctx: CanvasRenderingContext2D,
    objects: fabric.Object[],
    externalContentLevel: number
  ) {
    if (this.isHTMLExport()) return;

    // Restore to zero clip before recreating the clip state.
    ctx.restore();
    ctx.save();

    const vpt = this.vptCoords!;
    for (let i = externalContentLevel; i < objects.length; i++) {
      const object = objects[i];

      // Clip out the space for the object.
      ctx.beginPath();
      ctx.rect(vpt.tl.x, vpt.tl.y, vpt.br.x - vpt.tl.x, vpt.br.y - vpt.tl.y);
      if (!object.renderClipBoundary?.(ctx)) {
        ctx.rect(
          object.left,
          object.top,
          object.width * object.scaleX,
          object.height * object.scaleY
        );
      }
      ctx.clip('evenodd');
    }
  }

  // Show toolbar over selected objects.
  showObjectToolbar() {
    this.controller.actionShowObjectToolbar();
  }

  hideObjectToolbar() {
    this.controller.actionHideObjectToolbar();
  }

  beginningOperation() {
    this.controller.actionBeginObjectTransformation();
    this.hideObjectToolbar();
  }

  setDrawingCursor(cursor: string) {
    this.freeDrawingCursor = cursor;
    // Guard against potential race condition when tearing down canvas, where
    // we change connection before/after canvas is initialized/removed
    if (this.canvasElementInitialized) {
      this.setCursor(this.freeDrawingCursor);
    }
  }

  edgePanAnimationState: {
    // Second newest mouse event
    previousEvent: Event | null;
    // Newest mouse event
    latestEvent: Event | null;
    // Previous time, used to calculate delta time
    previousTime: DOMHighResTimeStamp | null;
    //  The direction of the animated pan movement
    vector: fabric.Point | null;
  } = {
    previousEvent: null,
    latestEvent: null,
    previousTime: null,
    vector: null,
  };

  calculateEdgePanVector(eventData: fabric.IEvent<Event>) {
    const state = this.edgePanAnimationState;

    state.latestEvent = eventData.e;

    const position = eventData.pointer!;
    const bounds = this.calcViewportBoundaries();

    const vector = new fabric.Point(0, 0);
    const zoom = this.getZoom();
    const threshold = 50 / zoom;
    const speed = 300;

    const rightDiff = Math.max(bounds.tr.x - position.x, 0);
    const leftDiff = Math.max(position.x - bounds.tl.x, 0);
    const upDiff = Math.max(position.y - bounds.tr.y, 0);
    const downDiff = Math.max(bounds.br.y - position.y, 0);

    if (rightDiff < threshold) {
      vector.x -= ((threshold - rightDiff) / threshold) * speed;
    } else if (leftDiff < threshold) {
      vector.x += ((threshold - leftDiff) / threshold) * speed;
    }

    if (upDiff < threshold) {
      vector.y += ((threshold - upDiff) / threshold) * speed;
    } else if (downDiff < threshold) {
      vector.y -= ((threshold - downDiff) / threshold) * speed;
    }

    if (!(vector.x || vector.y)) {
      state.vector = null;
      return;
    }

    if (state.vector === null) {
      state.vector = vector;
      this.beginEdgePanAnimation();
    } else {
      state.vector = vector;
    }
  }

  animateEdgePan = (time: DOMHighResTimeStamp) => {
    const state = this.edgePanAnimationState;

    if (!state.vector) return;

    if (state.latestEvent && state.latestEvent === state.previousEvent) {
      // When the animation pans the canvas beneath a stationary cursor,
      // we need to force fabric to rehandle the last triggered cursor
      // event with each frame so the object transformation continues
      // to follow the new relative cursor position.
      this.__onMouseMove(state.latestEvent as MouseEvent);

      // The event handler above may have updated the vector.
      if (!state.vector) return;
    }
    state.previousEvent = state.latestEvent;

    const dt = (time - state.previousTime!) / 1000;
    state.previousTime = time;

    this.relativePan(state.vector.multiply(dt));

    window.requestAnimationFrame(this.animateEdgePan);
  };

  relativePan(point: IPoint): Canvas {
    if (this.controller.allowPanAndZoom()) {
      super.relativePan(point);
      this.fire('pan');
    }
    return this;
  }

  beginEdgePanAnimation() {
    const state = this.edgePanAnimationState;
    state.previousTime = performance.now();
    window.requestAnimationFrame(this.animateEdgePan);
  }

  endEdgePanAnimation() {
    this.edgePanAnimationState.vector = null;
  }

  clearGroupSelector() {
    this._groupSelector = null;
    this.fire('groupSelector:cleared');
  }

  // Checks if a group selector full contains contains 'object'
  groupSelectorContainsObject(object: fabric.Object): boolean {
    if (this.groupSelectorTlBr) {
      const { tl, br } = this.groupSelectorTlBr;
      if (object.selectable && object.visible) {
        return object.isContainedWithinRect(tl, br, true);
      }
    }
    return false;
  }

  groupSelectorIntersectsObject(object: fabric.Object): boolean {
    if (this.groupSelectorTlBr) {
      const { tl, br } = this.groupSelectorTlBr;
      const { left, top, width, height } = object.getBoundingRect(true, true);
      const right = left + width;
      const bottom = top + height;
      if (
        object.selectable &&
        object.visible &&
        left < br.x &&
        right > tl.x &&
        top < br.y &&
        bottom > tl.y
      ) {
        return true;
      }
    }
    return false;
  }

  groupSelectorIntersectsOther(object: fabric.Object): boolean {
    if (this._groupSelector) {
      for (let i = 0, aboveObject = false; i < this._objects.length; i++) {
        const currentObject = this._objects[i];
        if (currentObject === object) {
          aboveObject = true;
          continue;
        }

        // Ignore all shapes that are behind the given one
        if (!aboveObject) continue;

        if (this.groupSelectorIntersectsObject(currentObject)) {
          return true;
        }
      }
    }
    return false;
  }

  // Checks if the user has clicked + dragged to create a selection box. This
  // allows for very small drags to be ignored as clicks
  get hasGroupSelector() {
    if (this._groupSelector) {
      return (
        Math.abs(this._groupSelector.left) > 6 ||
        Math.abs(this._groupSelector.top) > 6
      );
    }
    return false;
  }

  get groupSelectorTlBr(): { tl: fabric.Point; br: fabric.Point } | undefined {
    if (this._groupSelector) {
      const x1 = this._groupSelector.ex;
      const y1 = this._groupSelector.ey;
      const x2 = x1 + this._groupSelector.left;
      const y2 = y1 + this._groupSelector.top;
      const tl = new fabric.Point(Math.min(x1, x2), Math.min(y1, y2));
      const br = new fabric.Point(Math.max(x1, x2), Math.max(y1, y2));
      return { tl, br };
    }
  }

  // Pan and zoom with the wheel/touchpad
  _panAndZoomWheel(eventData: fabric.IEvent<WheelEvent>) {
    const event = eventData.e;

    // Setting to trackpad here by default, since before we didn't do detection
    // This will be removed when feature flag is removed
    let deviceType = 'trackpad';
    // Update device type based on the wheel event
    deviceType = this.deviceTypeDetector.detectDeviceType(event);

    if (!this.controller.allowPanAndZoom()) {
      return;
    }

    // metaKey = cmd on Mac, win key on Windows (which you can't hold down)
    if (
      event.altKey ||
      event.ctrlKey ||
      event.metaKey ||
      deviceType === 'mouse'
    ) {
      const delta = event.deltaY;
      const currentZoom = this.getZoom();
      const zoomRate = deviceType === 'mouse' ? 0.999 : 0.995;
      setZoom(
        this.controller,
        currentZoom * zoomRate ** delta,
        {
          x: event.offsetX,
          y: event.offsetY,
        },
        false
      );
      event.preventDefault();
      event.stopPropagation();
    } else {
      const vpt = this.viewportTransform!;
      vpt[4] -= event.deltaX;
      vpt[5] -= event.deltaY;
      this.setViewportTransform(vpt);
      event.preventDefault();
      event.stopPropagation();
      this.fire('pan');
    }

    // Clear the vpt hash to avoid reloading to the same location after moving around
    if (window.location.hash?.startsWith('#vpt-')) {
      history.replaceState(
        null,
        '',
        window.location.pathname + window.location.search
      );
    }

    // When the user has a selection rectangle going on at the same time,
    // we fire a mouse move event to force recalculating the selection.
    if (this._groupSelector) {
      // The zoom and pan operations force a re-render of the whole canvas,
      // including the selection. We set hasLostContext to false to prvent the
      // selection from being rendered twice.
      this.hasLostContext = false;
      this._onMouseMove(event);
    }

    this.temporaryHideObjectToolbar();

    // Moving the canvas means the cursor position changed.
    this.controller.trackCursor(event);
  }

  // Hide the toolbar while the canvas is moving, so that if the object toolbar
  // crosses under the cursor it doesn't start capturing the mouse events.
  temporaryHideObjectToolbar() {
    this.controller.actionHideObjectToolbar();
    clearTimeout(this._temporaryHideObjectToolbarTimer);
    this._temporaryHideObjectToolbarTimer = setTimeout(
      () => this.controller.actionShowObjectToolbar(),
      100
    );
  }

  // Duplicate the selected objects
  duplicateActiveSelection(
    moveNonLayoutMembers?: (objects: fabric.Object[]) => void
  ): fabric.Object | undefined {
    return this.duplicateObjects(this.getActiveObjects(), moveNonLayoutMembers);
  }

  // Duplicate objects
  // moveNonLayoutMembers is called after the new objects are created but before
  // the 'duplicated' event is fired. It is called with all objects that are not
  // inside an auto layout frame and might need their position adjusted.
  duplicateObjects(
    objects: fabric.Object | fabric.Object[],
    moveNonLayoutMembers?: (objects: fabric.Object[]) => void
  ): fabric.Object | undefined {
    const objectsToDuplicate = Array.isArray(objects) ? objects : [objects];
    if (!objectsToDuplicate.length) {
      return;
    }

    const copiedValue = copyWhiteboardContents({
      controller: this.controller,
      selection: objectsToDuplicate,
    });

    if (!copiedValue?.objectsAsJson) {
      return;
    }

    // Clone the objects in the selection.
    const activeObjectsJson = Serialization.transformWhiteboardContent(
      copiedValue.objectsAsJson,
      this.controller,
      true
    );

    // If the selection contained only objects that were removed, there
    // is nothing to duplicate.
    if (!activeObjectsJson || !Object.keys(activeObjectsJson.objects).length) {
      return;
    }

    const idMap = {};
    const clonedDoc = Serialization.cloneDocument(activeObjectsJson, idMap);
    Serialization.restoreDocument(this.controller, clonedDoc);

    const clonedObjects = Object.values(clonedDoc.objects).map(obj =>
      this.controller.dataStoreObjects.findRecordById(obj.id)
    );

    const objectsNotInLayout: fabric.Object[] = [];
    const nonLayoutFrames: (fabric.Object & FrameBehaviors)[] = [];

    // For all objects that belong to an auto layout frame, insert them below
    // the item they are a copy of.
    clonedObjects.forEach(clonedObject => {
      if (
        !this.updateClonedObjectLayout(clonedObject, idMap, objectsToDuplicate)
      ) {
        objectsNotInLayout.push(clonedObject);
      }
      // Separately aggregate frames that are not auto layouts.
      if (
        hasFrameBehaviors(clonedObject) &&
        clonedObject.frameBehaviorsEnabled &&
        !hasLayout(clonedObject)
      ) {
        // If the object is a frame, we need to start moving it with its contents.
        nonLayoutFrames.push(clonedObject);
      }
    });

    // Update the z index of any shape that is not in an auto layout so they
    // are all higher than all other objects in the original selection.
    if (objectsNotInLayout.length > 0) {
      const targetZ = getMaxZ(objectsToDuplicate);
      zManagerMove(objectsNotInLayout, { where: 'after', targetZ });

      if (moveNonLayoutMembers) {
        moveNonLayoutMembers(objectsNotInLayout);
      }
    }

    // Manually fire startMoveWithContents, which gets fired in mousedown in
    // FrameBehaviors. This ensure the frame moves its contents. The problem is
    // that duplicateActiveSelection is called in mousemove, so duplicated frames
    // never get the necessary mousedown event to start moving their contents.
    nonLayoutFrames?.forEach(obj => {
      obj.startMoveWithContents({
        e: { type: 'mousedown', altKey: true } as MouseEvent,
      });
    });

    // Select all the new objects
    const selection = this.makeActiveSelectionFromDocData(clonedDoc);
    selection.fire('duplicated');
    return selection;
  }

  private updateClonedObjectLayout(
    clonedObject: fabric.Object,
    idMap: Record<string, string>,
    objectsToDuplicate: fabric.Object[]
  ) {
    // Find duplicated objects that belong to auto layout frames
    if (
      !(
        hasFrameMemberBehaviors(clonedObject) &&
        clonedObject.container &&
        clonedObject.layoutPosition
      )
    ) {
      return false;
    }
    // Find the source object this was cloned from
    const sourceObject = objectsToDuplicate
      .filter(source => hasFrameMemberBehaviors(source))
      .find(source => source.id === idMap[clonedObject.id]);

    if (!sourceObject || !sourceObject.container) {
      return false;
    }

    // Get the layout for the sourceObject
    const layoutContainer = this.controller.dataStoreObjects.findRecordById(
      sourceObject.container
    );

    if (!hasLayout(layoutContainer)) {
      return false;
    }

    const children = layoutContainer.sortedChildren();
    const objectSiblingIndex = children.findIndex(
      child => child.id === clonedObject.id
    );
    const nextSibling = children[objectSiblingIndex + 1];

    // Generate new layout position after the source object
    const newPosition = generateJitteredKeyBetween(
      sourceObject.layoutPosition ?? null,
      nextSibling?.layoutPosition ?? null
    );
    clonedObject.set({ layoutPosition: newPosition });
    layoutContainer.layout();

    return true;
  }

  showBrowserMenu(event: MouseEvent): void {
    const allowMenuTargets = [
      '.aha-editor *',
      '.aha-editor-v2 *',
      `[class*="sidePanel"] *`,
      `[class*="sidePanelPullTab"], [class*="sidePanelPullTab"] *`,
    ];

    if (
      !allowMenuTargets.some(selector =>
        (event.target as HTMLElement)?.matches(selector)
      )
    ) {
      // If the click is not inside an editor or the sidebar menu then suppress the browser menu.
      event.preventDefault();
    }
  }

  _mouseClick(eventData: fabric.IEvent<MouseEvent>) {
    const objData = {};
    this.getObjects().forEach(object => {
      objData[object.type] ||= 0;
      objData[object.type]++;
    });
    const objSummary = Object.keys(objData)
      .map(key => `${key}=${objData[key]}`)
      .join(', ');

    console.log(
      `Canvas click. Total objects: ${this.size()}. ${objSummary}. ${this.controller.client.getTimesSinceLastRemoteOp()}`
    );

    if (!eventData.pointer) {
      console.warn('Could not determine pointer location');
      return;
    }

    if (isRightClick(eventData.e, this.lastMouseDown)) {
      // Try to select the target of the click.
      const { target } = eventData;
      if (target?.selectable && this.getActiveObject() !== target) {
        this.setActiveObject(target);
        this.requestRenderAll();
      } else if (!eventData.target) {
        this.discardActiveObject(eventData.e);
        this.requestRenderAll();
      }

      // Right button click on active object - show the object tool menu.
      // Right click to select object.
      const selection = this.getActiveObject();
      if (selection) {
        // See if the clicked control has a menu to show.
        if (eventData.target?.__corner) {
          const control = eventData.target.controls[eventData.target.__corner];
          if (control?.showRightClickMenu) {
            control.showRightClickMenu(
              this.controller,
              eventData.target,
              eventData.pointer.x,
              eventData.pointer.y
            );
            return;
          }
        }

        if (selection.type === 'table') {
          // don't show the table context menu if clicking the controls rendered around it, as we rely on click
          // position within the cell to determine which cell/row to change
          if (!eventData.target?.__corner) {
            this.controller.actionShowContextMenu(
              CONTEXT_MENU_TABLE,
              eventData.pointer.x,
              eventData.pointer.y
            );
          }
        } else {
          this.controller.actionShowContextMenu(
            CONTEXT_MENU_OBJECT,
            eventData.pointer.x,
            eventData.pointer.y
          );
        }
      } else {
        this.controller.actionShowContextMenu(
          CONTEXT_MENU_CANVAS,
          eventData.pointer.x,
          eventData.pointer.y
        );
      }
    }
  }

  _startRightClickPan(eventData: fabric.IEvent<MouseEvent>) {
    this.controller.actionSetPanStylus();
    this.freeDrawingBrush.onMouseDown(eventData.absolutePointer!, eventData);
    this._rightClickPan = true;
  }

  _endRightClickPan(eventData) {
    this.freeDrawingBrush.onMouseUp(eventData);
    this.controller.actionClearPanning();
    this._rightClickPan = false;
  }

  setObjectReadOnly(obj: fabric.Object & ReadOnlyBehaviors) {
    if (this.readOnly) {
      // If the whiteboard is readonly, all objects should be set to readonly.
      obj.setReadOnly(true);
      obj.hasControls = false;
    } else {
      if (this.allowUpdate) {
        // Users with allowUpdate can update all objects.
        obj.setReadOnly(false);
      } else if (this.allowAnnotate && isComment(obj)) {
        // Users with allowAnnotate can update comments that they authored
        obj.setReadOnly(obj.ownerId !== this.controller.state.userId);
      } else {
        // Anything else should be readonly
        obj.setReadOnly(true);
      }
    }
  }

  setReadOnly(readOnly: boolean, allowUpdate: boolean, allowAnnotate: boolean) {
    // Controls if group selection is enabled on the canvas.
    this.readOnly = readOnly;
    this.allowUpdate = allowUpdate;
    this.allowAnnotate = allowAnnotate;
    for (const element of this.controller.dataStoreObjects.records()) {
      if (hasReadOnlyBehaviors(element)) {
        this.setObjectReadOnly(element);
      }
    }

    const activeObject = this.getActiveObject();
    const activeObjects = this.getActiveObjects();
    // Remove any selection or current editing activity when switching.
    this.discardActiveObject();

    // Force render on transition to readOnly so that selection controls are cleared
    if (readOnly && (activeObject || activeObjects.length)) {
      this.requestRenderAll();
    }
  }

  // Add support for resizing in custom control "corners"
  _getOriginFromCorner(target: string, corner: AttachmentPoint) {
    // @ts-expect-error fabric private method
    const origin = super._getOriginFromCorner(target, corner);

    if (corner === AttachmentPoint.Left) {
      origin.x = 'right';
    }
    if (corner === AttachmentPoint.Right) {
      origin.x = 'left';
    }

    return origin;
  }

  updateSelectionRotationControlVisibility(eventData: fabric.IEvent) {
    const selection = eventData.target;
    if (selection && isSimpleSelection(selection)) {
      selection.setRotationControlVisibility();
    }
  }

  handleLockedObjectsInSelection(eventData: fabric.IEvent) {
    const selection = eventData.target;
    if (selection && isSimpleSelection(selection)) {
      selection.handleLockedObjectSelection();
    }
  }

  _setupErrorHandler(eventHandler: string) {
    const original = this[eventHandler];
    if (!original) return;

    this[eventHandler] = (...args) => {
      try {
        original(...args);
      } catch (e) {
        if (!this.canvasElementInitialized) {
          // If the canvas is not fully initialized, we probably shouldn't be
          // running event handlers yet. We have been receiving Sentries
          // https://aha-labs-inc.sentry.io/issues/5009093363?project=139823
          // that might be avoided in that case. Continue to rasie the Sentry
          // for now and testing the assumption that canvasElementInitialized
          // is false.
          console.warn(
            'Canvas not fully initialized. Event handler should be skipped'
          );
        }
        this.controller.actionHandleError(e);
      }
    };
  }

  /**
   * Performs a depth-first search of the canvas objects, returning the first
   * object that matches the provided predicate.
   *
   * @param predicate A function that returns true if the object matches
   * @param includeGroups Whether to also search inside groups
   * @param objects The objects to search. If not provided, the top level objects are searched
   * @returns The first object that matches the predicate, or undefined if none match
   */
  findFirstObject<T extends fabric.Object = fabric.Object>(
    predicate: (object: fabric.Object) => object is T,
    includeGroups = true,
    objects = this.getObjects()
  ): T | undefined {
    for (let i = objects.length - 1; i >= 0; i--) {
      const object = objects[i];
      if (includeGroups && isSimpleGroup(object)) {
        const found = this.findFirstObject(
          predicate,
          true,
          object.getObjects()
        );
        if (found) {
          return found;
        }
      } else {
        if (predicate(object)) {
          return object;
        }
      }
    }

    return undefined;
  }

  setupErrorHandling() {
    [
      '_onResize',
      '_onMouseDown',
      '_onMouseMove',
      '_onMouseOut',
      '_onMouseEnter',
      '_onMouseWheel',
      '_onDoubleClick',
      '_onDragOver',
      '_onDragEnter',
      '_onDragLeave',
      '_onDrop',
    ].forEach(event => this._setupErrorHandler(event));
  }

  get canvasElementInitialized() {
    return this.upperCanvasEl && this.lowerCanvasEl;
  }

  private objectChangedInterval?: ReturnType<typeof setInterval>;
  private objectChangedAt: number | undefined;
  private readonly changedFrameIds = new Set<string>();

  private setupObjectChanged() {
    // debounce object:changed via an interval rather than a timeout. this is less precise
    // (since the debounce period varies) but much more efficient
    this.objectChangedInterval = setInterval(() => {
      if (this.objectChangedAt && this.objectChangedAt < Date.now() - 400) {
        this.fire('object:changed', {
          changedFrameIds: this.changedFrameIds,
        });

        this.changedFrameIds.clear();
        this.objectChangedAt = undefined;
      }
    }, 400);
  }

  private teardownObjectChanged() {
    clearInterval(this.objectChangedInterval);
  }

  public objectChanged(object: fabric.Object) {
    // track if frames or their contents change, and send the collated frame ids with the event
    // for granular cache invalidation
    if (isFrame(object)) {
      this.changedFrameIds.add(object.id);
    } else if (hasFrameMemberBehaviors(object) && object.container) {
      this.changedFrameIds.add(object.container);
    }

    this.objectChangedAt = Date.now();
    this.controller.renderManager.refreshObject(object);
  }

  makeActiveSelectionFromDocData(docData: DocData) {
    const objects = Serialization.getUngroupedObjects(this, docData);
    return this.makeActiveSelection(objects);
  }

  makeActiveSelection(objects: fabric.Object[]) {
    let selectedObject: fabric.Object;
    if (objects.length === 1) {
      selectedObject = objects[0];
    } else {
      selectedObject = new fabric.ActiveSelection(
        SimpleSelection.getFilteredObjectsForSelection(objects),
        {
          canvas: this,
        }
      ) as fabric.Object;
    }

    this.setActiveObject(selectedObject);

    return selectedObject;
  }

  /**
   * Identical to getPointer but fixes an issue where the point is incorrect when
   * mouse pointer is overlapping another HTML element on top of the canvas
   *
   * Returns pointer coordinates relative to canvas.
   * Can return coordinates with or without viewportTransform.
   * ignoreZoom false gives back coordinates that represent
   * the point clicked on canvas element.
   * ignoreZoom true gives back coordinates after being processed
   * by the viewportTransform ( sort of coordinates of what is displayed
   * on the canvas where you are clicking.
   * ignoreZoom true = HTMLElement coordinates relative to top,left
   * ignoreZoom false, default = fabric space coordinates, the same used for shape position
   * To interact with your shapes top and left you want to use ignoreZoom true
   * most of the time, while ignoreZoom false will give you coordinates
   * compatible with the object.oCoords system.
   * of the time.
   * @param {Event} e
   * @param {Boolean} ignoreZoom
   * @return {Object} object with "x" and "y" number values
   */
  getUncachedPointer(e: MouseEvent, ignoreZoom?: boolean): IPoint {
    (this as any)._resetTransformEventData();
    return this.getPointer(e, ignoreZoom);
  }

  /**
   * Tests if the current viewport is empty of objects
   */
  isViewportEmpty(): boolean {
    for (const object of this.getObjects()) {
      if (object.isOnScreen()) {
        return false;
      }
    }

    return true;
  }

  /**
   * Override for fabric's canvas findTarget method to handle custom layout
   * selection logic. When a layout is selected, clicking and dragging anywhere
   * within the layout (including contained objects) will keep the layout selected
   */
  findTarget(e: Event, skipGroup: boolean): fabric.Object | undefined {
    // Get the standard target using fabric's default logic
    const target = super.findTarget(e, skipGroup);
    const activeObject = this.getActiveObject();

    if (this.shouldSelectLayoutInsteadOfChild(target, activeObject)) {
      return activeObject;
    }

    return target;
  }

  private shouldSelectLayoutInsteadOfChild(
    target: fabric.Object | undefined,
    activeObject: fabric.Object
  ): boolean {
    if (!target || !activeObject || !hasLayout(activeObject)) {
      return false;
    }

    // Track visited frames to avoid infinite loop. This can happen because of
    // collab changes that might reorder the frame hierarchy
    const visited = new Set<string>();

    // Traverse up the hierarchy from the target to see if the activeObject is an ancestor.
    let current = target;
    while (hasFrameMemberBehaviors(current) && current.container) {
      if (visited.has(current.id)) {
        // Circular dependency detected
        console.warn('Circular dependency detected in layout hierarchy');
        return false;
      }
      visited.add(current.id);

      if (current.container === activeObject.id) {
        return true;
      }
      current = this.controller.dataStoreObjects.findRecordById(
        current.container
      );
    }

    return false;
  }

  fireSelectionChangeCallbacks() {
    this.selectionChangeListeners.forEach(callback =>
      callback(this.getActiveObjects())
    );
  }
}

export default Canvas;
